import { action, observable, reaction } from 'mobx'
import {
  CustomersApi,
  CustomersHelper,
  IDriversLicenseNormalizedData,
} from '@getgreenline/customers'
import dayjs from 'dayjs'
import { POSStore } from './POSStore'
import { GlobalStore } from './GlobalStore'
import { barcodeStore } from '@/app/components/BarcodeStore'

export enum AgeVerificationScanStatus {
  Idle = 'idle',
  Scanning = 'scanning',
  Processing = 'processing',
  Success = 'success',
  FailMinAge = 'fail_min_age',
  FailHardware = 'fail_hardware',
}

export class AgeVerificationStore {
  @observable shouldDisplayAgeVerificationModal = false
  @observable showAgeVerificationWarning = false
  @observable customerAge?: number = undefined
  @observable scanStatus: AgeVerificationScanStatus = AgeVerificationScanStatus.Idle
  @observable scanResult: IDriversLicenseNormalizedData | null = null
  @observable cameraScanMode = false
  @observable cameraScanStopStream = false
  private isScanCallbackSet = false

  constructor(private posStore: POSStore) {
    this.reactToPaymentLines()
  }

  reactToPaymentLines() {
    reaction(
      () => ({
        paymentLines: this.posStore.currentPayment?.paymentLines || [],
        maxAgeRequirement: this.maxAgeRequirement,
      }),
      ({ paymentLines, maxAgeRequirement }) => {
        if (paymentLines.length === 0 || maxAgeRequirement === undefined) {
          this.resetAgeVerification()

          const paymentVerification = this.posStore.currentPayment?.paymentVerification
          if (paymentVerification) {
            this.posStore.currentPayment?.setPaymentVerification(null)
          }
        }
      },
    )
  }

  @action
  setShouldDisplayAgeVerificationModal = (display: boolean) => {
    this.shouldDisplayAgeVerificationModal = display
  }

  @action
  setShowAgeVerificationWarning = (showAgeVerificationWarning: boolean) => {
    this.showAgeVerificationWarning = showAgeVerificationWarning
  }

  @action
  setCustomerAge(scanResult: IDriversLicenseNormalizedData | null) {
    const age = scanResult?.dob
      ? CustomersHelper.getAgeByDriversLicenseDob(scanResult.dob)
      : undefined
    this.customerAge = age
  }

  resetAgeVerification() {
    this.setShowAgeVerificationWarning(false)
    this.setCustomerAge(null)
  }

  @action
  setScanStatus = (scanStatus: AgeVerificationScanStatus) => {
    this.scanStatus = scanStatus
  }

  @action
  setScanResult = (scanResult: IDriversLicenseNormalizedData | null) => {
    this.scanResult = scanResult
  }

  @action
  resetScanState = () => {
    this.scanStatus = AgeVerificationScanStatus.Idle
    this.scanResult = null
  }

  @action
  stopScan = () => {
    if (!this.isScanCallbackSet) return
    barcodeStore.setBackToPrevBarcodeCallback()
    barcodeStore.setBackToPrevScanActivityCallbacks()
    this.isScanCallbackSet = false
  }

  @action
  startScan = (platformUrl?: string) => {
    this.stopScan()
    this.resetScanState()
    this.isScanCallbackSet = true

    // Set a longer timeout for barcode scanning to accommodate slower scanners
    barcodeStore.setTimeoutDuration(150)
    barcodeStore.setScanActivityCallbacks(
      () => {
        if (this.scanStatus === AgeVerificationScanStatus.Idle) {
          this.setScanStatus(AgeVerificationScanStatus.Scanning)
        }
      },
      () => {
        if (this.scanStatus === AgeVerificationScanStatus.Scanning) {
          this.setScanStatus(AgeVerificationScanStatus.Idle)
        }
      },
    )
    barcodeStore.setBarcodeCallback(async (barcode) => {
      try {
        this.setScanStatus(AgeVerificationScanStatus.Processing)
        if (!platformUrl) {
          this.setScanResult(null)
          this.setScanStatus(AgeVerificationScanStatus.FailHardware)
          return
        }

        const normalizedData = await CustomersApi.getDriversLicenseNormalizedData(platformUrl, {
          aamva_pdf417: barcode,
        })

        this.setScanResultAndStatus(normalizedData)
      } catch (error) {
        this.setScanResult(null)
        this.setScanStatus(AgeVerificationScanStatus.FailHardware)
      } finally {
        this.stopScan()
        barcodeStore.setTimeoutDuration()
      }
    })
  }

  @action
  parseCameraScanData = async (barcode: string, platformUrl?: string) => {
    try {
      this.setScanStatus(AgeVerificationScanStatus.Processing)
      if (!platformUrl) {
        this.setScanResult(null)
        this.setScanStatus(AgeVerificationScanStatus.FailHardware)
        return
      }

      const normalizedData = await CustomersApi.getDriversLicenseNormalizedData(platformUrl, {
        aamva_pdf417: barcode,
      })

      this.setScanResultAndStatus(normalizedData)
    } catch (error) {
      this.setScanResult(null)
      this.setScanStatus(AgeVerificationScanStatus.FailHardware)
    } finally {
      this.setCameraScanMode(false)
      this.setCameraScanStopStream(false)
    }
  }

  @action
  setScanResultAndStatus = (scanResult: IDriversLicenseNormalizedData) => {
    this.scanResult = scanResult

    const maxAgeRequirement = this.maxAgeRequirement
    const dobString = scanResult.dob

    if (!dobString || dobString.length !== 8) {
      this.scanStatus = AgeVerificationScanStatus.FailHardware
      return
    }

    const age = CustomersHelper.getAgeByDriversLicenseDob(dobString)

    this.scanStatus =
      !maxAgeRequirement || age >= maxAgeRequirement
        ? AgeVerificationScanStatus.Success
        : AgeVerificationScanStatus.FailMinAge
  }

  @action
  setCameraScanMode = (cameraScanMode: boolean) => {
    this.cameraScanMode = cameraScanMode
  }

  @action
  setCameraScanStopStream = (stopStream: boolean) => {
    this.cameraScanStopStream = stopStream
  }

  isDriversLicenseExpired = (expDate?: string | null): boolean => {
    if (!expDate || expDate.length < 8) return false

    const formattedExpiryDate = CustomersHelper.formatDriversLicenseDob(expDate)
    const expiryDate = dayjs(formattedExpiryDate, 'YYYY-MM-DD', true)
    if (!expiryDate.isValid()) return false

    return dayjs().isAfter(expiryDate, 'day')
  }

  get maxAgeRequirement(): undefined | number {
    if (GlobalStore.isGreenlineApp || !this.posStore.currentPayment) {
      return
    }

    let maxAgeRequirement: number | undefined = undefined

    this.posStore.currentPayment.paymentLines.forEach((line) => {
      const category = this.posStore.productStore.categories?.find((category) => {
        const categoryIdToCheck = line.parentCategoryId ?? line.categoryId

        return category.id === categoryIdToCheck
      })

      if (!category || category.defaultMinimumAge === null) {
        return
      }

      if (!maxAgeRequirement || category.defaultMinimumAge > maxAgeRequirement) {
        maxAgeRequirement = category.defaultMinimumAge
      }
    })

    return maxAgeRequirement
  }
}
